import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import copy from 'copy-to-clipboard';
import { FieldArray, Formik, getIn } from 'formik';
import { isIP } from 'is-ip';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Grid from '~/components/core/Atomic/Grid/Grid';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import Tooltip from '~/components/core/Atomic/Tooltip';
import Typography from '~/components/core/Atomic/Typography';
import CancelButton from '~/components/core/Buttons/CancelButton';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import TextField from '~/components/core/Molecules/Fields/TextField';
import RadioWithButtonWrapper from '~/components/core/RadioWithButtonWrapper';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import Switch from '~/components/ui/atoms/Switch';
import { ROLES_TYPES_DICT } from '~/Types';
import { isUserFiveSigma } from '~/UserUtils';

import { reportAxiosError } from '../../Utils';
import CardDialog from '../CardDialog';
import { useCms } from '../hooks/useCms';
import { CheckIcon, CopyToClipboardIcon, InfoIcon, PencilIcon, TrashIcon_Deprecated, WarningIcon } from '../icons';
import LoadingIndicator from '../LoadingIndicator';
import TextFieldFormik from '../TextFieldFormik';
import useDataFetcher from '../useDataFetcher';

import SessionTimeoutConfiguration from './LoginConfiguration/SessionTimeoutConfiguration';
import OperationsBreadcrumbs from './OperationsBreadcrumbs';
import { useSysconfig } from './SystemConfigurationScreen';

import { useStyles } from '../../assets/styles';

const SSOOptions = [
  { text: 'No SSO', value: 'none', isConfigurable: false },
  { text: 'Azure', value: 'azure_ad', isConfigurable: true },
  { text: 'Okta', value: 'okta', isConfigurable: true },
  { text: 'OpenID Connect (generic)', value: 'oidc', isConfigurable: true },
];

const initialSSOValues = {
  scope: '',
  domain: '',
  tenant: '',
  issuer: '',
  provider: '',
  client_id: '',
  client_secret: '',
};

const LoginConfiguration = () => {
  return (
    <CardDialog noCardTitle>
      <OperationsBreadcrumbs currentTab="Login Configuration" />
      <Typography variant="subtitle1" style={{ padding: '14px 0px', fontSize: '18px' }}>
        AUTHENTICATION CONFIGURATION
      </Typography>
      <span>Manage login configuration and authentication</span>
      <UsernameAndPasswordConfiguration />
      <GSuiteConfiguration />
      <SSOMethods />
      <SessionTimeoutConfiguration />
    </CardDialog>
  );
};

const UsernameAndPasswordConfiguration = () => {
  const { organization, users } = useSysconfig();
  const [isMFADialogOpen, setIsMFADialogOpen] = useState(false);
  const [isPasswordLoginDialogOpen, setIsPasswordLoginDialogOpen] = useState(false);
  const { classes } = useStyles();
  const theme = useTheme();
  const { isLoading, isError, data, reloadData } = useDataFetcher(
    `/sysconfig/api/v1/organizations/${organization.id}/login_configurations`
  );

  const Header = () => (
    <Typography
      display="block"
      variant="subtitle2"
      style={{ paddingTop: '15px', fontWeight: 'bold', fontSize: '16px' }}
    >
      Username and Password
    </Typography>
  );

  if (isLoading || isError) {
    return (
      <>
        <Header />
        <LoadingIndicator isError={isError} />
      </>
    );
  }

  // System/service accounts (hidden role types) don't authenticate via MFA, so they are excluded.
  const activeUsersWithoutPhone = users.reduce(
    (count, user) =>
      !user.is_removed && !user.mobile_phone_number && !ROLES_TYPES_DICT[user.role?.role_type]?.is_hidden
        ? count + 1
        : count,
    0
  );

  const UsernameAndPasswordPhonesWarning = () => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="body2" style={{ marginBottom: '14px', fontSize: '14px', color: '#606060' }}>
        Update mobile phone number in <span style={{ fontWeight: 'bold' }}>User Management</span>
      </Typography>
      {activeUsersWithoutPhone > 0 && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon iconColor={theme.palette.warning.main} />
          <Typography variant="body2" style={{ fontSize: '14px', marginLeft: '5px', color: '#606060' }}>
            There are {activeUsersWithoutPhone} active users without phone number configuration
          </Typography>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Header />
      <Typography variant="body2" style={{ fontSize: '14px', color: '#606060', margin: '10px 0px 5px 0px' }}>
        Enabled by default
      </Typography>
      <div style={{ display: 'flex', marginBottom: '15px' }}>
        {!data.password_login_disabled && <CheckIcon iconColor="#0CBC8B" />}
        <span style={{ marginLeft: !data.password_login_disabled ? '7px' : undefined, marginRight: '12px' }}>
          Sign in with password {data.password_login_disabled ? 'is disabled' : 'is enabled'}
        </span>
        <PencilIcon
          data-testid="edit-password-login"
          style={{ cursor: 'pointer' }}
          className={classes.hoverableNonFilledIcon}
          onClick={() => setIsPasswordLoginDialogOpen(true)}
        />
      </div>
      <div style={{ display: 'flex', marginBottom: '15px' }}>
        {!data.allow_login_username_password_no_mfa && <CheckIcon iconColor="#0CBC8B" />}
        <span
          style={{ marginLeft: !data.allow_login_username_password_no_mfa ? '7px' : undefined, marginRight: '12px' }}
        >
          Authentication with MFA {!data.allow_login_username_password_no_mfa ? '' : 'is disabled'}
        </span>
        <PencilIcon
          style={{ cursor: 'pointer' }}
          className={classes.hoverableNonFilledIcon}
          onClick={() => setIsMFADialogOpen(true)}
        />
      </div>
      {!data.allow_login_username_password_no_mfa && (
        <>
          <UsernameAndPasswordPhonesWarning />
          {data.static_ip_list.length > 0 && (
            <>
              <Typography
                variant="body2"
                style={{ fontSize: '14px', marginTop: '15px', fontWeight: 600, color: '#606060' }}
              >
                MFA with IP White List
              </Typography>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {data.static_ip_list.map((item) => (
                  <span key={item} style={{ marginRight: '10px', marginBottom: '10px' }}>
                    <TextField className={classes.textField} label="IP Address" disabled value={item} />
                  </span>
                ))}
              </div>
            </>
          )}
        </>
      )}
      {isMFADialogOpen && (
        <EditUserNameAndPasswordDialog
          onClose={() => setIsMFADialogOpen(false)}
          data={data}
          reloadData={reloadData}
          UsernameAndPasswordPhonesWarning={UsernameAndPasswordPhonesWarning}
        />
      )}
      {isPasswordLoginDialogOpen && (
        <EditPasswordLoginDialog
          onClose={() => setIsPasswordLoginDialogOpen(false)}
          data={data}
          reloadData={reloadData}
        />
      )}
    </>
  );
};

const GSuiteConfiguration = () => {
  return (
    <div style={{ marginTop: '40px' }}>
      <Typography
        display="block"
        variant="subtitle2"
        style={{ paddingTop: '15px', fontWeight: 'bold', fontSize: '16px' }}
      >
        G-Suite
      </Typography>
      <Typography variant="body2" style={{ fontSize: '14px', color: '#606060', margin: '10px 0px 5px 0px' }}>
        Enabled by default, no need to configure.
      </Typography>
    </div>
  );
};

const putSSO = async (organization, values) =>
  await axios.put(`/sysconfig/api/v1/organizations/${organization.id}/login_configurations/sso`, values);

const CopyableUrlField = ({ label, value }) => {
  const { classes } = useStyles();
  const theme = useTheme();

  return (
    <>
      <Typography variant="body2" style={{ fontSize: '14px', color: '#606060', marginBottom: '8px' }}>
        {label}
      </Typography>
      <TextField
        className={classes.textField}
        value={value}
        disabled
        fullWidth
        style={{ maxWidth: '500px' }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip arrow placement="top" title="Copy to clipboard">
                <IconButton size="small" onClick={() => copy(value)}>
                  <CopyToClipboardIcon iconColor={theme.palette.primary.darker} />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
    </>
  );
};

CopyableUrlField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

const SSOFullIdpResponseLogging = ({ data, reloadData }) => {
  const { organization } = useSysconfig();

  // Five Sigma only, and rendered rather than disabled for everyone else: what this writes to the
  // application log is the IdP's access token, its ID token and the user's own address. It is a
  // diagnostic for a login an IdP will not complete, not a setting a customer administrator owns.
  return (
    <div style={{ marginTop: '24px', borderTop: '1px solid #E0E0E0', paddingTop: '16px' }}>
      <Typography variant="subtitle2" style={{ fontSize: '14px', fontWeight: 'bold' }}>
        Five Sigma only
      </Typography>
      <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
        <Switch
          checked={data.sso_full_idp_response_logging || false}
          aria-label="Log full SSO IdP responses"
          onChange={async () => {
            try {
              await axios.put(
                `/sysconfig/api/v1/organizations/${organization.id}/login_configurations/sso_full_idp_response_logging`,
                { sso_full_idp_response_logging: !data.sso_full_idp_response_logging }
              );
              await reloadData();
            } catch (error) {
              reportAxiosError(error);
            }
          }}
        />
        <span style={{ fontSize: '14px', color: '#606060' }}>
          Log full SSO IdP responses &mdash; writes the raw token and userinfo responses, including access and ID tokens
          and the user&apos;s email address, to the application log. Turn off once diagnosed; it also turns itself off
          24 hours after being switched on.
        </span>
      </div>
    </div>
  );
};

SSOFullIdpResponseLogging.propTypes = {
  data: PropTypes.object.isRequired,
  reloadData: PropTypes.func.isRequired,
};

const SSOMethods = () => {
  const [methodChanged, setMethodChanged] = useState(null);
  const { organization } = useSysconfig();
  const { user } = useCms();
  const { isLoading, isError, data, reloadData } = useDataFetcher(
    `/sysconfig/api/v1/organizations/${organization.id}/login_configurations`
  );

  const Header = () => (
    <Typography
      display="block"
      variant="subtitle2"
      style={{ paddingTop: '15px', fontWeight: 'bold', fontSize: '16px' }}
    >
      Single Sign On (SSO) Methods
    </Typography>
  );

  if (isLoading || isError) {
    return (
      <>
        <Header />
        <LoadingIndicator isError={isError} />
      </>
    );
  }

  const method = data?.sso?.provider || 'none';
  const methodObject = SSOOptions.find((option) => option.value === method);

  return (
    <div style={{ marginTop: '40px' }}>
      <Header />
      <Typography variant="body2" style={{ fontSize: '14px', color: '#606060', margin: '10px 0px 5px 0px' }}>
        Choose SSO method:
      </Typography>
      {SSOOptions.map(({ value, text }) => (
        <RadioWithButtonWrapper
          key={value}
          name="sso_method"
          text={text}
          value={value}
          checked={method === value}
          onChange={async () => {
            setMethodChanged(value);
            if (value === 'none') {
              await putSSO(organization, { sso: {}, logout_redirect_page: '' });
              await reloadData();
            }
          }}
          wrapperOverrideStyle={{ '&:not(:hover)': { backgroundColor: '#FAFAFA' } }}
          disabled={isLoading}
        />
      ))}
      {methodObject?.isConfigurable && data?.direct_login_url && (
        <div style={{ marginTop: '24px' }}>
          <CopyableUrlField label="Direct SSO login URL:" value={data.direct_login_url} />
          {method === 'oidc' && data?.oidc_callback_url && (
            <div style={{ marginTop: '16px' }}>
              <CopyableUrlField label="Redirect URI to register at the IdP:" value={data.oidc_callback_url} />
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', marginTop: '16px' }}>
            <Switch
              checked={data.sso_login_url_only || false}
              onChange={async () => {
                await axios.put(
                  `/sysconfig/api/v1/organizations/${organization.id}/login_configurations/sso_login_url_only`,
                  { sso_login_url_only: !data.sso_login_url_only }
                );
                await reloadData();
              }}
            />
            <span style={{ fontSize: '14px', color: '#606060' }}>
              Allow SSO login only via direct URL (disables email-based SSO login)
            </span>
          </div>
          {isUserFiveSigma(user) && <SSOFullIdpResponseLogging data={data} reloadData={reloadData} />}
        </div>
      )}
      {methodChanged && SSOOptions.find((option) => option.value === methodChanged)?.isConfigurable && (
        <EditSSOMethodDialog
          data={data}
          reloadData={reloadData}
          method={methodChanged}
          onClose={() => {
            setMethodChanged(null);
          }}
        />
      )}
    </div>
  );
};

const EditUserNameAndPasswordDialog = ({ reloadData, data = {}, onClose, UsernameAndPasswordPhonesWarning }) => {
  const { organization } = useSysconfig();
  const { classes } = useStyles();

  return (
    <Formik
      initialValues={{
        mfa_enabled: !data.allow_login_username_password_no_mfa,
        static_ip_list: data.static_ip_list,
      }}
      validationSchema={Yup.object().shape({
        static_ip_list: Yup.array().of(Yup.string().test('is-possible-ip', 'Invalid IP', (ip) => isIP(ip))),
      })}
      enableReinitialize
      onSubmit={async (values, formikProps) => {
        try {
          await axios.put(`/sysconfig/api/v1/organizations/${organization.id}/login_configurations/mfa`, {
            allow_login_username_password_no_mfa: !values.mfa_enabled,
          });
          await axios.put(`/sysconfig/api/v1/organizations/${organization.id}/login_configurations/static_ip`, values);
          await reloadData();
          onClose();
        } catch (error) {
          await reportAxiosError(error);
          formikProps.setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, isSubmitting, values }) => {
        return (
          <CardDialog isDialog title="Edit Username and Password" onClose={onClose}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <SwitchFormik
                  id="mfa_enabled"
                  label={
                    <div>
                      <div style={{ display: 'flex' }}>
                        Authentication with MFA
                        <Tooltip
                          arrow
                          placement="top"
                          title={
                            <div>
                              <div>MFA- multi-factor authentication</div>
                              <div>Disabling MFA will reduce security and is not recommended</div>
                            </div>
                          }
                        >
                          <span style={{ display: 'flex' }}>
                            <InfoIcon iconColor="#909090" size={16} style={{ marginLeft: '6px' }} />
                          </span>
                        </Tooltip>
                      </div>
                      <Typography variant="body2" style={{ marginBottom: '14px', fontSize: '14px', color: '#606060' }}>
                        {!values.mfa_enabled && (
                          <span>
                            Disabling Multi Factor Authentication will reduce security and is not recommended.
                          </span>
                        )}
                        {values.mfa_enabled && (
                          <span>For Multi Factor Authentication mobile phone number or IP is required.</span>
                        )}
                      </Typography>
                    </div>
                  }
                />
              </Grid>
              <Grid item xs={12} container style={{ marginLeft: '60px' }}>
                {values.mfa_enabled && (
                  <>
                    <Grid item xs={12} style={{ marginTop: '-30px' }}>
                      <Typography variant="body2" style={{ fontSize: '14px', marginTop: '14px', fontWeight: 600 }}>
                        MFA with mobile phone number
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <UsernameAndPasswordPhonesWarning />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" style={{ fontSize: '14px', marginTop: '14px', fontWeight: 600 }}>
                        MFA with IP white list
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FieldArray
                        name="static_ip_list"
                        render={({ remove, push }) => (
                          <>
                            {getIn(values, 'static_ip_list').map((_, idx) => (
                              <Grid container key={idx} style={{ marginRight: '5px', marginBottom: '5px' }}>
                                <Grid item xs={6}>
                                  <TextFieldFormik
                                    className={classes.textField}
                                    id={`static_ip_list.${idx}`}
                                    label="IP Address"
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={1}>
                                  <span style={{ marginLeft: '10px' }} onClick={() => remove(idx)}>
                                    <TrashIcon_Deprecated
                                      iconColor="202020"
                                      className={classes.hoverableIcon}
                                      style={{ marginTop: '28px' }}
                                    />
                                  </span>
                                </Grid>
                                <Grid item xs={5} />
                              </Grid>
                            ))}
                            <Button
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={() => push('')}
                              style={{ display: 'flex' }}
                            >
                              <span style={{ marginRight: '5px' }}>ADD IP ADDRESS</span>
                            </Button>
                          </>
                        )}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
              <Grid container style={{ paddingTop: '8px' }}>
                <div className={classes.buttonsContainer}>
                  <CancelButton disabled={isSubmitting} onClick={onClose} contained />
                  <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting}>
                    Save
                  </Button>
                </div>
              </Grid>
            </Grid>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

EditUserNameAndPasswordDialog.propTypes = {
  reloadData: PropTypes.func.isRequired,
  data: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  UsernameAndPasswordPhonesWarning: PropTypes.func.isRequired,
};

const EditPasswordLoginDialog = ({ reloadData, data = {}, onClose }) => {
  const { organization } = useSysconfig();
  const { classes } = useStyles();

  return (
    <Formik
      initialValues={{ password_login_enabled: !data.password_login_disabled }}
      enableReinitialize
      onSubmit={async (values, formikProps) => {
        try {
          await axios.put(
            `/sysconfig/api/v1/organizations/${organization.id}/login_configurations/password_login_disabled`,
            { password_login_disabled: !values.password_login_enabled }
          );
          await reloadData();
          onClose();
        } catch (error) {
          await reportAxiosError(error);
          formikProps.setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, isSubmitting, values }) => {
        return (
          <CardDialog isDialog title="Edit Sign In With Password" onClose={onClose}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <SwitchFormik
                  id="password_login_enabled"
                  label={
                    <div>
                      <div style={{ display: 'flex' }}>
                        Sign in with password
                        <Tooltip
                          arrow
                          placement="top"
                          title={
                            <div>
                              <div>Controls whether users can sign in with a username and password.</div>
                              <div>
                                When disabled, users can still sign in with G-Suite, or SSO if it is configured.
                              </div>
                            </div>
                          }
                        >
                          <span style={{ display: 'flex' }}>
                            <InfoIcon iconColor="#909090" size={16} style={{ marginLeft: '6px' }} />
                          </span>
                        </Tooltip>
                      </div>
                      <Typography variant="body2" style={{ marginBottom: '14px', fontSize: '14px', color: '#606060' }}>
                        {!values.password_login_enabled && (
                          <span>
                            Password login is disabled. Users will be able to sign in with G-Suite, or SSO if it is
                            configured.
                          </span>
                        )}
                        {values.password_login_enabled && (
                          <span>Users can sign in with their username and password.</span>
                        )}
                      </Typography>
                    </div>
                  }
                />
              </Grid>
              {!values.password_login_enabled && (
                <Grid item xs={12} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <InfoIcon iconColor="#909090" size={16} style={{ marginRight: '6px', marginTop: '2px' }} />
                  <Typography variant="body2" style={{ fontSize: '14px', color: '#606060' }}>
                    Users who are already signed in will stay logged in — this change only affects new sign-in attempts.
                  </Typography>
                </Grid>
              )}
              <Grid container style={{ paddingTop: '8px' }}>
                <div className={classes.buttonsContainer}>
                  <CancelButton disabled={isSubmitting} onClick={onClose} contained />
                  <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting}>
                    Save
                  </Button>
                </div>
              </Grid>
            </Grid>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

EditPasswordLoginDialog.propTypes = {
  reloadData: PropTypes.func.isRequired,
  data: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

const EditSSOMethodDialog = ({ reloadData, method, data = {}, onClose }) => {
  const { organization } = useSysconfig();
  const { classes } = useStyles();

  const getValidationByMethod = (requiredMethod) => {
    if (requiredMethod === method) {
      return Yup.string().required('Required');
    }
    return Yup.string();
  };

  return (
    <Formik
      initialValues={{
        sso_method: method,
        sso: { ...initialSSOValues, ...data.sso },
        logout_redirect_page: data.logout_redirect_page || '',
      }}
      validationSchema={Yup.object().shape({
        sso_method: Yup.string().oneOf(['okta', 'azure_ad', 'oidc']),
        sso: Yup.object().shape({
          scope: Yup.string(),
          domain: getValidationByMethod('okta'),
          tenant: getValidationByMethod('azure_ad'),
          issuer: getValidationByMethod('oidc'),
          client_id: Yup.string().required('Required'),
          client_secret: Yup.string().required('Required'),
        }),
        logout_redirect_page: Yup.string(),
      })}
      enableReinitialize
      onSubmit={async (values, formikProps) => {
        try {
          await putSSO(organization, {
            sso: { ...values.sso, provider: values.sso_method },
            logout_redirect_page: values.logout_redirect_page,
          });
          await reloadData();
          onClose();
        } catch (error) {
          await reportAxiosError(error);
          formikProps.setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, isSubmitting }) => {
        return (
          <CardDialog
            isDialog
            title={`Edit ${SSOOptions.find((option) => option.value === method)?.text || ''} SSO Method`}
            onClose={onClose}
          >
            <Grid container spacing={1}>
              <span>{`In order to login with ${
                SSOOptions.find((option) => option.value === method)?.text || ''
              } add an OpenID Connect (OIDC) web app integration.`}</span>
              {method === 'oidc' && data.oidc_callback_url && data.direct_login_url && (
                // Shown before anything is saved: the IdP application has to be registered with
                // these URLs first, and only then does it issue the Client ID and Client Secret
                // the fields below ask for.
                <Grid item xs={12}>
                  <CopyableUrlField label="Redirect URI to register at the IdP:" value={data.oidc_callback_url} />
                  <div style={{ marginTop: '16px' }}>
                    <CopyableUrlField label="Login URL for the IdP application:" value={data.direct_login_url} />
                  </div>
                </Grid>
              )}
              <Grid item xs={6}>
                <TextFieldFormik
                  className={classes.textField}
                  id="sso.scope"
                  label="Scope"
                  helperText={method === 'oidc' ? 'Leave empty to request openid email profile' : ''}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} />
              {method === 'okta' && (
                <Grid item xs={6}>
                  <TextFieldFormik className={classes.textField} id="sso.domain" label="Domain" fullWidth />
                </Grid>
              )}
              {method === 'azure_ad' && (
                <Grid item xs={6}>
                  <TextFieldFormik className={classes.textField} id="sso.tenant" label="Tenant" fullWidth />
                </Grid>
              )}
              {method === 'oidc' && (
                <Grid item xs={6}>
                  <TextFieldFormik
                    className={classes.textField}
                    id="sso.issuer"
                    label="Issuer URL"
                    helperText="For example https://oauth.id.jumpcloud.com"
                    fullWidth
                  />
                </Grid>
              )}
              <Grid item xs={6} />
              <Grid item xs={6}>
                <TextFieldFormik
                  className={classes.textField}
                  id="sso.client_id"
                  label="Client ID"
                  type="password"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} />
              <Grid item xs={6}>
                <TextFieldFormik
                  className={classes.textField}
                  id="sso.client_secret"
                  label="Client Secret"
                  type="password"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} />
              <Grid item xs={6}>
                <TextFieldFormik
                  className={classes.textField}
                  id="logout_redirect_page"
                  label="Logout redirect page"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} />
              <Grid container style={{ paddingTop: '8px' }}>
                <div className={classes.buttonsContainer}>
                  <CancelButton disabled={isSubmitting} onClick={onClose} autoFocus contained />
                  <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting}>
                    Save
                  </Button>
                </div>
              </Grid>
            </Grid>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

EditSSOMethodDialog.propTypes = {
  reloadData: PropTypes.func.isRequired,
  method: PropTypes.string.isRequired,
  data: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default LoginConfiguration;
